import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonaAutocomplete from "../../Empresa/NuevaFactura/InfoInvoice/PersonaAutocomplete";

const tdLabelSx = {
  width: 180,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top"
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };

const getCSRFToken = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(name + "=")) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
};

const InfoPurchase = ({ onChange }) => {
  const fileRef = useRef(null);
  const [vendedores, setVendedores] = useState([]);

  const [data, setData] = useState({
    fecha_emision: "",
    tipo_persona: "PROVEEDOR",
    tipo_documento: "FACTURA",
    numero_documento: "",
    autorizacion: "",
    persona: null,
    referencia: "",
    vendedor: "",
    vencimiento: "0",
    asumir_iva: 0
  });

  useEffect(() => {
    if (!onChange) return;

    const payload = {
      ...data,
      persona: data.persona
        ? { id: data.persona.persona_id || data.persona.id }
        : null
    };

    onChange(payload);
  }, [data]);

  useEffect(() => {
    const cargarInicial = async () => {
      try {
        const r = await fetch("/facturas/documento/detalle/", {
          credentials: "include"
        });
        const j = await r.json();
        if (r.ok) {
          setData((prev) => ({
            ...prev,
            fecha_emision: j.fecha_emision || ""
          }));
        }
      } catch {}
    };

    const cargarVendedores = async () => {
      try {
        const r = await fetch("/facturas/usuarios/activos/", {
          credentials: "include"
        });
        const j = await r.json();
        if (r.ok && Array.isArray(j.vendedores)) {
          setVendedores(j.vendedores);
        }
      } catch {
        setVendedores([]);
      }
    };

    cargarInicial();
    cargarVendedores();
  }, []);

  const handleChange = (field) => (e) => {
    setData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleXML = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("xml", file);

    const res = await fetch("/documentos/compras/cargar-xml/", {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRFToken": getCSRFToken() },
      body: formData
    });

    const json = await res.json();

    if (res.ok) {
      setData((prev) => ({
        ...prev,
        tipo_persona: "PROVEEDOR",
        tipo_documento: "FACTURA",
        fecha_emision: json.fecha_emision || "",
        autorizacion: json.autorizacion || "",
        numero_documento: json.numero_documento || "",
        persona: json.proveedor || null
      }));
    }

    e.target.value = "";
  };

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Información del Documento
      </Typography>

      <input
        type="file"
        accept=".xml"
        hidden
        ref={fileRef}
        onChange={handleXML}
      />

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField
                value={data.fecha_emision}
                onChange={handleChange("fecha_emision")}
                size="small"
                sx={{ width: 220 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Tipo de Persona:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.tipo_persona}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    tipo_persona: e.target.value,
                    persona: null
                  }))
                }
                size="small"
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
                <MenuItem value="CLIENTE">Cliente</MenuItem>
              </Select>
            </TableCell>

            <TableCell sx={{ ...tdFieldSx, pl: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudUploadIcon />}
                sx={{ minWidth: 160 }}
                onClick={() => fileRef.current.click()}
              >
                Cargar XML
              </Button>
            </TableCell>

            <TableCell sx={tdFieldSx}>
              <Select
                value={data.tipo_documento}
                size="small"
                disabled
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="FACTURA">Factura</MenuItem>
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.numero_documento}
                onChange={handleChange("numero_documento")}
                size="small"
                sx={{ width: 280 }}
              />
            </TableCell>

            <TableCell sx={tdLabelSx}>Autorización:</TableCell>
            <TableCell sx={{ p: 1, width: "40%" }}>
              <TextField
                value={data.autorizacion}
                onChange={handleChange("autorizacion")}
                size="small"
                sx={{ width: 500 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Persona:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <PersonaAutocomplete
                value={data.persona}
                tipoDocumento={data.tipo_persona}
                onChange={(persona) =>
                  setData((prev) => ({ ...prev, persona }))
                }
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField
                value={data.referencia}
                onChange={handleChange("referencia")}
                size="small"
                sx={{ width: 350 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <Select
                value={data.vendedor}
                onChange={handleChange("vendedor")}
                size="small"
                displayEmpty
                sx={{ width: 260 }}
              >
                <MenuItem value="">
                  <em>Seleccionar</em>
                </MenuItem>
                {vendedores.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.nombre}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vencimiento (días):</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.vencimiento}
                onChange={handleChange("vencimiento")}
                size="small"
                sx={{ width: 220 }}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default InfoPurchase;
